import { DatePickerInput } from '@mantine/dates'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty, isNil } from '@semios/app-platform-common'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { TLngLat } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { Dispatch, SetStateAction } from 'react'
import { colors } from 'settings/colors'
import { isSemiosEmployeeOrTester } from 'utils/isSemiosEmployeeOrTester'
import { getPropertyForSelectedStation } from '../_utils/getPropertyForSelectedStation'
import { pwpFromFieldCapacity } from '../_utils/pwpFromFieldCapacity'
import { DepthSelect } from './DepthSelect'
import { GoodReadingsButton } from './GoodReadingsButton'
import { OverridesTable } from './OverridesTable'
import { PwpFcNumberInput } from './PwpFcNumberInput'
import { RemoveOverridesButton } from './RemoveOverridesButton'
import { StationSelect } from './StationSelect'
import { VWCChart } from './VWCChart'

export const SoilCalibrationModalContents = ({
  data,
  dates,
  fc,
  handleDateChange,
  handleDepthChange,
  handleStationChange,
  isDeleting,
  lastGoodReading,
  overrides,
  pwp,
  selectedDepth,
  selectedStation,
  setFc,
  setIsDeleting,
  setLastGoodReading,
  setOverrides,
  setPwp,
  soilProbeDepthsAvailable,
}: {
  data: NonNullable<routes.Values.Response['points']>[string]['values']['soilVwc']
  dates: [Date | null, Date | null]
  fc: number | ''
  handleDateChange: (dates: [Date | null, Date | null]) => void
  handleDepthChange: (depth: string | null) => void
  handleStationChange: (station: TLngLat) => void
  isDeleting: boolean
  lastGoodReading?: string
  overrides?: routes.SoilCalibrationSettingsGet.Response['overrides']
  pwp: number | ''
  selectedDepth: number | null
  selectedStation: TLngLat | null
  setFc: Dispatch<SetStateAction<number | ''>>
  setIsDeleting: Dispatch<SetStateAction<boolean>>
  setLastGoodReading: Dispatch<SetStateAction<routes.SoilCalibrationSettingsGet.Response['lastGoodReading']>>
  setOverrides: Dispatch<SetStateAction<routes.SoilCalibrationSettingsGet.Response['overrides']>>
  setPwp: Dispatch<SetStateAction<number | ''>>
  soilProbeDepthsAvailable: string[]
}) => {
  const hasSoilDateOverridePermission =
    selectedPropertyHasPermission({
      permission: 'EDIT_SOIL_DATE_OVERRIDE_SETTINGS',
    }) || isSemiosEmployeeOrTester()

  const hasSoilCalibrationPermission =
    selectedPropertyHasPermission({
      permission: 'EDIT_SOIL_CALIBRATION_SETTINGS',
    }) || isSemiosEmployeeOrTester()

  const propertyForSelectedStation = getPropertyForSelectedStation(selectedStation)

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 20 }}>
      <h5 css={{ margin: 0, padding: 0 }}>{propertyForSelectedStation?.propertyName}</h5>
      <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <StationSelect selectedStation={selectedStation} onStationChange={handleStationChange} />

        <DepthSelect
          availableDepths={soilProbeDepthsAvailable}
          selectedDepth={selectedDepth}
          onDepthChange={handleDepthChange}
        />
        <DatePickerInput
          styles={{
            input: {
              textAlign: 'center',
            },
          }}
          w={300}
          onChange={(newDates) => {
            if (!newDates || (Array.isArray(newDates) && newDates.every((date) => date === null))) {
              return
            }

            handleDateChange(newDates)
          }}
          type="range"
          value={dates}
          valueFormat={translate.dates.getMomentFormat('MMMM D, YYYY')}
          maxDate={moment.tz(moment.tz.guess()).toDate()}
        />
      </div>
      {selectedDepth && (
        <>
          <VWCChart
            dates={dates}
            fc={fc}
            lastGoodReading={lastGoodReading}
            pwp={pwp}
            selectedStation={selectedStation}
            vwcData={
              !data || isEmpty(data) ? null : data.find((d) => Number(d.metadata.depth) === selectedDepth)
            }
          />
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <PwpFcNumberInput
                label={translate.phrases.abbreviations('Field Capacity')}
                value={fc}
                setValue={(newFc) => {
                  setFc(newFc)

                  if (isNil(newFc) || newFc === '') {
                    return
                  }

                  const automaticPWP = Math.max(pwpFromFieldCapacity(newFc), 0) // Ensure PWP is not negative

                  setPwp(automaticPWP)
                }}
                color={colors.blue}
              />
              <PwpFcNumberInput
                label={translate.phrases.abbreviations('Permanent Wilting Point')}
                value={pwp}
                setValue={setPwp}
                color={colors.red}
              />
            </div>
            {hasSoilDateOverridePermission && (
              <GoodReadingsButton setLastGoodReading={setLastGoodReading} lastGoodReading={lastGoodReading} />
            )}
          </div>
          {!isEmpty(overrides) && hasSoilCalibrationPermission && (
            <RemoveOverridesButton
              isDeleting={isDeleting}
              selectedStation={selectedStation}
              setIsDeleting={setIsDeleting}
              setFc={setFc}
              setPwp={setPwp}
              setOverrides={setOverrides}
            />
          )}
          <OverridesTable overrides={overrides} soilProbeDepthsAvailable={soilProbeDepthsAvailable} />
        </>
      )}
    </div>
  )
}
